import React, { useState, useEffect } from "react";
import DashboardLayout from "../DashboardLayout";
import OnboardingPage from "./Onboarding/OnboardingPage";
import ApprovalsPage from "./Onboarding/ApprovalsPage";
import { Briefcase, UserPlus, CheckSquare } from "lucide-react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../Login/firebase";
import apiClient from "../../utils/apiClient";
import server from "../../utils/serverConfig";

const CRM_SECTIONS = [
  { id: "onboarding", label: "Onboarding", icon: UserPlus, description: "Onboard new clients and manage broker accounts" },
  { id: "approvals", label: "Approvals", icon: CheckSquare, description: "Review and approve submitted broker accounts" },
];

export default function CRM() {
  const [user] = useAuthState(auth);
  const [userAccess, setUserAccess] = useState(() => {
    try {
      const cached = localStorage.getItem("user_access");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  useEffect(() => {
    const fetchAccess = async () => {
      if (user && user.email) {
        try {
          const response = await apiClient.get(`user-access?email=${user.email}`);
          if (response.data.status === "success") {
            setUserAccess(response.data.access);
            localStorage.setItem("user_access", JSON.stringify(response.data.access));
          }
        } catch (error) {
          console.error("Error fetching real-time access (CRM):", error);
          const cached = localStorage.getItem("user_access");
          if (cached) setUserAccess(JSON.parse(cached));
        }
      }
    };

    fetchAccess();
  }, [user]);

  const [activeSection, setActiveSection] = useState("onboarding");

  const isAdmin = userAccess?.role === "admin";
  const sections = CRM_SECTIONS.filter((s) => s.id !== "approvals" || isAdmin);

  // Poll for pending approvals count if user is admin
  useEffect(() => {
    if (!isAdmin) return;

    const fetchPendingCount = async () => {
      try {
        const response = await apiClient.get(`${server.godseye.baseUrl}crm/onboarding/approvals`);
        if (response.data) {
          setPendingApprovalsCount(response.data.length);
        }
      } catch (err) {
        console.error("Error fetching approvals count in CRM sidebar:", err);
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 15000); // poll every 15 seconds
    return () => clearInterval(interval);
  }, [isAdmin]);

  useEffect(() => {
    if (userAccess && userAccess.role !== "admin" && activeSection === "approvals") {
      setActiveSection("onboarding");
    }
  }, [userAccess, activeSection]);

  return (
    <DashboardLayout>
      <div className="flex w-full h-screen bg-[#0a0c14] overflow-hidden">
        {/* CRM Left Sidebar */}
        <div className="w-52 flex-shrink-0 border-r border-zinc-800/60 bg-[#0d0f18] flex flex-col">
          {/* CRM Header */}
          <div className="px-4 py-4 border-b border-zinc-800/60">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                <Briefcase className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h2 className="text-white text-sm font-bold tracking-wide">CRM</h2>
                <p className="text-zinc-600 text-[10px]">Client Management</p>
              </div>
            </div>
          </div>

          {/* CRM Nav Items */}
          <nav className="flex flex-col gap-1 p-2 flex-1">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              const hasBadge = section.id === "approvals" && pendingApprovalsCount > 0;

              return (
                <button
                  key={section.id}
                  id={`crm-nav-${section.id}`}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-start gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200 group border ${
                    isActive
                      ? "bg-blue-600/15 border-blue-600/30 text-white"
                      : "text-zinc-500 hover:bg-zinc-800/50 hover:text-zinc-300 border-transparent"
                  }`}
                >
                  <Icon
                    className={`w-4 h-4 mt-0.5 flex-shrink-0 transition-colors ${
                      isActive ? "text-blue-400" : "text-zinc-600 group-hover:text-zinc-400"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-xs font-semibold ${isActive ? "text-white" : ""}`}>{section.label}</p>
                      {hasBadge && (
                        <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white font-extrabold text-[10px] flex items-center justify-center animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.4)]">
                          {pendingApprovalsCount}
                        </span>
                      )}
                    </div>
                    <p className={`text-[10px] leading-relaxed mt-0.5 ${isActive ? "text-zinc-400" : "text-zinc-600"}`}>
                      {section.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Content Header */}
          <div className="px-6 py-4 border-b border-zinc-800/60 bg-[#0d0f18]">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-white text-lg font-bold">
                  {sections.find((s) => s.id === activeSection)?.label || "CRM"}
                </h1>
                <p className="text-zinc-500 text-xs mt-0.5">
                  {sections.find((s) => s.id === activeSection)?.description}
                </p>
              </div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs text-zinc-600">
                <span>CRM</span>
                <span>/</span>
                <span className="text-zinc-400 capitalize">{activeSection}</span>
              </div>
            </div>
          </div>

          {/* Section Content */}
          <div className="flex-1 overflow-hidden">
            {activeSection === "onboarding" && <OnboardingPage />}
            {activeSection === "approvals" && isAdmin && (
              <ApprovalsPage onApprovalsChange={setPendingApprovalsCount} />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
